import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAdmin } from "./useAdmin";
import { useExcludedIP } from "./useExcludedIP";

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem("session_id");
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem("session_id", sessionId);
  }
  return sessionId;
};

export const useClickTracking = () => {
  const [sessionId] = useState(getSessionId());
  const { isAdmin, isLoading: isAdminLoading } = useAdmin();
  const { isExcluded: isExcludedIP, isChecking: isCheckingIP } = useExcludedIP();

  const trackClick = useCallback(
    async (
      eventType: "link" | "button" | "cta" | "social" | "navigation" | "newsletter" | "video" | "blog" | "instagram" | "about" | "shop",
      elementLabel: string,
      elementUrl?: string
    ) => {
      // Don't track if running in iframe (Lovable preview)
      if (window.self !== window.top) {
        return;
      }

      // Wait for IP check to complete
      if (isCheckingIP) {
        return;
      }

      if (isExcludedIP) {
        return;
      }

      // Wait for admin check to complete
      if (isAdminLoading) {
        return;
      }

      // Don't track admin users
      if (isAdmin) {
        return;
      }

      try {
        const pagePath = window.location.pathname;
        const userAgent = navigator.userAgent;

        // Call edge function for server-side tracking with IP capture
        await supabase.functions.invoke("track-click", {
          body: {
            event_type: eventType,
            element_label: elementLabel,
            element_url: elementUrl,
            page_path: pagePath,
            user_agent: userAgent,
            session_id: sessionId,
          },
        });
      } catch (error) {
        // Silently fail to not disrupt user experience
      }
    },
    [sessionId, isAdmin, isAdminLoading, isExcludedIP, isCheckingIP]
  );

  return { trackClick };
};
